import { useState } from "react";
import { Modal, Button } from "@/admin/ui";

/**
 * One-time credential display. Shows newly created/reset user credentials
 * (email + cleartext password) EXACTLY ONCE. The password is never fetched again.
 * Pass either a single { email, password, roleLabel } or an `entries` array of them.
 */
export default function CredentialModal({ open, onClose, email, password, roleLabel = "", entries }) {
    const [copied, setCopied] = useState("");

    const list = Array.isArray(entries) && entries.length
        ? entries
        : (email ? [{ email, password, roleLabel }] : []);

    async function copy(text, key) {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(key);
            setTimeout(() => setCopied(""), 1500);
        } catch (_e) {
            /* clipboard blocked — user can still select manually */
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={list.length > 1 ? "Credentials created" : "Credentials created"}
            size="sm"
            testid="credential-modal"
            footer={
                <Button onClick={onClose} data-testid="credential-done-btn">
                    Done
                </Button>
            }
        >
            <div className="space-y-5" data-testid="credential-body">
                <div className="rounded-2xl border border-amber-200 bg-amber-50/70 text-amber-800 text-[13px] px-4 py-3">
                    Copy and share these credentials securely now. For security, the password is shown{" "}
                    <span className="font-semibold">only once</span> and cannot be retrieved later.
                </div>

                {list.map((c, idx) => (
                    <div key={idx} className="space-y-3 rounded-2xl border border-black/10 bg-neutral-50/60 p-4" data-testid={`credential-entry-${idx}`}>
                        {c.roleLabel && (
                            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-violet-700">
                                {c.roleLabel}
                            </div>
                        )}
                        <div>
                            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-neutral-500">
                                Login (email / username)
                            </div>
                            <div className="mt-1.5 flex items-center gap-2">
                                <code
                                    data-testid={`credential-email-${idx}`}
                                    className="flex-1 rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm text-black break-all"
                                >
                                    {c.email}
                                </code>
                                <Button size="sm" variant="subtle" onClick={() => copy(c.email, `email-${idx}`)} data-testid={`credential-copy-email-${idx}`}>
                                    {copied === `email-${idx}` ? "Copied" : "Copy"}
                                </Button>
                            </div>
                        </div>
                        <div>
                            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-neutral-500">
                                Temporary password
                            </div>
                            <div className="mt-1.5 flex items-center gap-2">
                                <code
                                    data-testid={`credential-password-${idx}`}
                                    className="flex-1 rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm font-semibold text-black break-all"
                                >
                                    {c.password}
                                </code>
                                <Button size="sm" variant="subtle" onClick={() => copy(c.password, `pw-${idx}`)} data-testid={`credential-copy-password-${idx}`}>
                                    {copied === `pw-${idx}` ? "Copied" : "Copy"}
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}

                <div className="text-[12px] text-neutral-500">
                    The user will be required to set their own password on first login.
                </div>
            </div>
        </Modal>
    );
}
